/**
 * A view's records as a page reads them (docs/views.md#6-the-page-api), in one implementation for the runtime's data host
 * (src/client/data-host.ts) and the frame's copy (src/client/frame/store.ts): the partitions a collection lives in for
 * a connection, finding and getting records, `can` by the rules the server applies (rules.ts), the check a write passes
 * before it goes, and the record a link names. Each side hands it its records: the runtime from its connection's copies
 * of the partitions, the frame from what the runtime sent it, each with its own unanswered writes laid over them.
 */
import { type Query, run } from "./query";
import type { Rec, UsedCollection } from "./records";
import {
  allowed,
  type CollectionRules,
  type Place,
  parsePartition,
  partFor,
  partsFor,
  placeWriter,
  type Writer,
} from "./rules";

/** A record as a page reads it: with the chat whose partition holds it, or null outside a chat's. */
export type CopyRow = Rec & { chat: string | null };
export type WriteKind = "insert" | "update" | "put" | "remove";

/** What a side of the copy gives it. */
export interface CopySource {
  /** Where this connection's data is, or null before the view's object has said. */
  place(): Place | null;
  rules(collection: string): CollectionRules;
  /** The partitions this side holds, by key. */
  parts(): string[];
  /** The collections a partition holds. */
  collections(part: string): string[];
  /** A partition's records of a collection, each with its chat, this side's unanswered writes laid over them. */
  rows(part: string, collection: string): CopyRow[];
  /** Another view's collection, which the manifest names in `uses`, as the view's object sent it. */
  used(collection: string): UsedCollection | null;
  /** The section that shows a collection, by the manifest. */
  section(collection: string): string | null;
}

/** Why a write can't go, with the code the page's promise rejects with. */
export interface WriteRefusal {
  error: string;
  code: "invalid" | "forbidden";
}
/** A write that may go: the partition it goes to, the record before it, and the action the rules judged. */
export interface WritePlan {
  part: string;
  before: CopyRow | null;
  action: "insert" | "update" | "remove";
}

export class RecordCopy {
  constructor(private readonly source: CopySource) {}

  /** The partitions a collection's records live in for this connection: in the home, each chat's. */
  partsOf(collection: string): string[] {
    const place = this.source.place();
    if (!place || collection.includes("/")) return [];
    const held = new Set(this.source.parts());
    return partsFor(this.source.rules(collection), place).filter((key) => held.has(key));
  }
  /** The one partition a write or a record goes to: the record's chat, the chat named, or the only one; null when that's unclear. */
  partOf(collection: string, chat?: string | null): string | null {
    const place = this.source.place();
    if (!place || collection.includes("/")) return null;
    const key = partFor(this.source.rules(collection), place, chat);
    return key && this.source.parts().includes(key) ? key : null;
  }
  private writer(): Writer | null {
    const place = this.source.place();
    return place ? placeWriter(place) : null;
  }

  /** One partition's records of a collection, as a page reads them. */
  rowsIn(part: string, collection: string): CopyRow[] {
    return this.source.rows(part, collection);
  }
  /** A collection's records, from each partition it lives in, or another view's from `uses`, read-only. */
  private rows(collection: string): CopyRow[] {
    if (collection.includes("/")) return this.source.used(collection)?.records ?? [];
    return this.partsOf(collection).flatMap((part) => this.source.rows(part, collection));
  }
  find(collection: string, query: Query = {}): CopyRow[] {
    return run(this.rows(collection), query, this.source.place()?.writer.id);
  }
  get(collection: string, id: string): CopyRow | undefined {
    return this.rows(collection).find((row) => row.id === id);
  }

  /** Whether a write would be allowed. In the home, an insert that names no chat asks whether any of its chats would take it. */
  can(
    collection: string,
    action: string,
    record: (Rec & { chat?: string | null }) | null,
    options: { chat?: string } = {},
  ): boolean {
    const writer = this.writer();
    if (!writer || !["insert", "update", "remove"].includes(action)) return false;
    const chat = record?.chat ?? options.chat;
    const parts = chat || record ? [this.partOf(collection, chat)] : this.partsOf(collection);
    const rules = this.source.rules(collection);
    return parts.some(
      (part) =>
        !!part &&
        allowed(
          rules,
          parsePartition(part)!,
          { kind: action as "insert" | "update" | "remove", id: record?.id ?? "", before: record },
          writer,
        ),
    );
  }

  /**
   * The check a write passes before it goes anywhere: a record stays in its chat, a new one goes to the chat named,
   * which the home needs (docs/views.md#1-views), it goes to a partition this side holds, and the rules allow it.
   */
  plan(collection: string, kind: WriteKind, id: string, options: { chat?: string } = {}): WritePlan | WriteRefusal {
    const found = this.get(collection, id);
    const before = found ?? null;
    if (found && options.chat && found.chat && found.chat !== options.chat)
      return { error: "A record stays in its chat.", code: "invalid" };
    const part = this.partOf(collection, found?.chat ?? options.chat);
    if (!part) return { error: this.nowhere(collection, options.chat), code: options.chat ? "invalid" : "forbidden" };
    const action = kind === "put" ? (before ? "update" : "insert") : kind;
    if (!this.can(collection, action, before, { chat: found?.chat ?? options.chat }))
      return { error: `Not allowed to ${action} in ${collection}.`, code: "forbidden" };
    return { part, before, action };
  }
  /** Why a write has nowhere to go. */
  private nowhere(collection: string, chat?: string): string {
    if (chat) return "Write to a chat this view is in.";
    return this.source.rules(collection).scope === "chat" && this.partsOf(collection).length > 1
      ? "Choose a chat for the new record: insert(fields, { chat })."
      : `This view cannot write ${collection} here.`;
  }

  /**
   * The record a link names, by id, and the section that shows it: this view's own first, then another view's from
   * `uses`, with that view's link, where the record opens.
   */
  locate(
    id: string,
    uses: readonly string[],
  ): { collection: string; section: string | null; link?: string | null } | null {
    for (const part of this.source.parts())
      for (const collection of this.source.collections(part))
        if (this.source.rows(part, collection).some((row) => row.id === id))
          return { collection, section: this.source.section(collection) };
    for (const collection of uses) {
      const used = this.source.used(collection);
      if (used?.records.some((row) => row.id === id)) return { collection, section: used.section, link: used.link };
    }
    return null;
  }
}

/** Whether a write's check refused it. */
export const refused = (outcome: WritePlan | WriteRefusal): outcome is WriteRefusal => "error" in outcome;
